import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'

const VerifyMfaPage = () => {
  const { t } = useTranslation('auth')
  const [code, setCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const {
    verifyMfaCode,
    verifyRecoveryCode,
    cancelMfaChallenge,
    user,
    loading: authLoading,
    requiresMfaVerification,
    mfaChallengeUser
  } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Get the user from location state (passed from login)
  const { user: pendingUser } = location.state || {}

  // Redirect to dashboard once user is authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, authLoading, navigate])

  // Redirect to login if no MFA challenge is pending
  useEffect(() => {
    if (!authLoading && !requiresMfaVerification && !pendingUser) {
      navigate('/login', { replace: true })
    }
  }, [authLoading, requiresMfaVerification, pendingUser, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      let result
      if (useRecoveryCode) {
        result = await verifyRecoveryCode(code)
        if (result.success && result.message) {
          // Recovery code verified - redirect to login with message
          navigate('/login', {
            state: { message: result.message },
            replace: true
          })
          return
        }
      } else {
        result = await verifyMfaCode(code)
      }

      if (result.success) {
        // Don't navigate here - let the useEffect handle it after user state is set
        return
      } else {
        setError(result.error || t('errors.verificationFailed'))
      }
    } catch (err) {
      setError(err.message || t('errors.verificationFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    await cancelMfaChallenge()
    navigate('/login', { replace: true })
  }

  const toggleRecoveryCode = () => {
    setUseRecoveryCode(!useRecoveryCode)
    setCode('')
    setError('')
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '1rem',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        padding: '2rem',
        backgroundColor: 'white',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ marginBottom: '1rem', textAlign: 'center' }}>
          {t('verifyMfa.title')}
        </h1>

        <p style={{
          marginBottom: '2rem',
          textAlign: 'center',
          color: '#666',
          fontSize: '0.9rem'
        }}>
          {useRecoveryCode
            ? t('verifyMfa.enterRecoveryCode')
            : t('verifyMfa.enterAuthenticatorCode')}
        </p>

        {mfaChallengeUser && (
          <p style={{
            marginBottom: '1rem',
            textAlign: 'center',
            color: '#666',
            fontSize: '0.85rem'
          }}>
            {t('verifyMfa.signingInAs', { email: mfaChallengeUser })}
          </p>
        )}

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            backgroundColor: '#fee',
            color: '#c00',
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              {useRecoveryCode ? t('verifyMfa.recoveryCodeLabel') : t('verifyMfa.authenticationCodeLabel')}
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => {
                if (useRecoveryCode) {
                  // Auto-capitalize and auto-insert dash for recovery codes
                  let value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                  // Insert dash after 4 characters
                  if (value.length > 4) {
                    value = value.slice(0, 4) + '-' + value.slice(4, 8)
                  }
                  setCode(value)
                } else {
                  // Only allow digits for TOTP codes
                  setCode(e.target.value.replace(/\D/g, ''))
                }
              }}
              placeholder={useRecoveryCode ? t('verifyMfa.recoveryCodePlaceholder') : t('verifyMfa.authCodePlaceholder')}
              required
              autoComplete="one-time-code"
              inputMode={useRecoveryCode ? 'text' : 'numeric'}
              pattern={useRecoveryCode ? '[A-Z0-9]{4}-[A-Z0-9]{4}' : '[0-9]{6}'}
              maxLength={useRecoveryCode ? 9 : 6}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                fontSize: '1.25rem',
                textAlign: 'center',
                letterSpacing: '0.25rem'
              }}
              disabled={loading}
              autoFocus
            />
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              marginBottom: '1rem'
            }}
            disabled={loading}
          >
            {loading ? t('verifyMfa.verifying') : t('verifyMfa.verify')}
          </button>
        </form>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <button
            type="button"
            onClick={toggleRecoveryCode}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              cursor: 'pointer',
              fontSize: '0.9rem',
              textDecoration: 'underline'
            }}
          >
            {useRecoveryCode
              ? t('verifyMfa.useAuthenticatorApp')
              : t('verifyMfa.useRecoveryCode')}
          </button>

          <button
            type="button"
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {t('verifyMfa.cancelLogin')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VerifyMfaPage
